import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import OfferCardAdmin from "../components/OfferCardAdmin";
import API from "../config/api";
import "../css/OffertAdmin.css";

const OffertAdmin = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [hasChanges, setHasChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // Check authentication
    const token = localStorage.getItem("authToken");
    const expiry = localStorage.getItem("tokenExpiry");

    if (!token || !expiry || Date.now() > parseInt(expiry)) {
      localStorage.removeItem("authToken");
      localStorage.removeItem("tokenExpiry");
      navigate("/login");
      return;
    }

    fetchOffers();
  }, [navigate]);

  const fetchOffers = async () => {
    try {
      setLoading(true);
      const response = await fetch(API.ENDPOINTS.OFFERS);
      const data = await response.json();
      setOffers(data || []);
      setError(null);
    } catch (err) {
      console.error("Error fetching offers:", err);
      setError("Impossibile caricare le offerte");
    } finally {
      setLoading(false);
    }
  };

  const handleAddOffer = () => {
    const newOffer = {
      id: Date.now(),
      title: "Nuova Offerta",
      description: "Descrizione dell'offerta",
      hasImage: false,
      imagePath: "",
      hasLink: false,
      linkUrl: "",
      linkText: "",
      prezzo: "€ 0",
      uploadDate: new Date().toISOString(),
    };

    setOffers([...offers, newOffer]);
    setHasChanges(true);
  };

  const handleDeleteOffer = (id) => {
    setOffers(offers.filter((offer) => offer.id !== id));
    setDeleteConfirm(null);
    setHasChanges(true);
  };

  const handleFieldChange = (id, field, value) => {
    // Use functional updater to avoid stale state when multiple updates
    // (e.g. imagePath and hasImage) are applied in quick succession.
    setOffers((prevOffers) =>
      prevOffers.map((offer) =>
        offer.id === id ? { ...offer, [field]: value } : offer
      )
    );
    setHasChanges(true);
  };

  const handleImageUpload = async (id, file) => {
    if (!file) return;

    const formData = new FormData();
    formData.append("image", file);

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API.ENDPOINTS.UPLOAD, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await response.json();

      if (data.success) {
        handleFieldChange(id, "imagePath", data.path);
        handleFieldChange(id, "hasImage", true);
      } else {
        alert("Errore durante l'upload: " + data.error);
      }
    } catch (err) {
      console.error("Upload error:", err);
      alert("Errore durante l'upload dell'immagine");
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);

    try {
      const token = localStorage.getItem("authToken");
      const response = await fetch(API.ENDPOINTS.OFFERS, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(offers),
      });

      const data = await response.json();

      if (data.success) {
        setHasChanges(false);
        alert("Modifiche salvate con successo!");
      } else {
        alert("Errore durante il salvataggio: " + data.error);
      }
    } catch (err) {
      console.error("Save error:", err);
      alert("Errore durante il salvataggio delle modifiche");
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem("authToken");
    localStorage.removeItem("tokenExpiry");
    navigate("/login");
  };

  if (loading) {
    return (
      <div>
        <div className="admin-container">
          <div className="loading-message">
            <div className="spinner"></div>
            <p>Caricamento in corso...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        <div className="admin-container">
          <div className="error-message">
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="admin-container">
        <div className="admin-header">
          <h1>Gestione Offerte</h1>
          <div className="admin-actions">
            <Link to="/admin/settings" className="btn btn--secondary">
              ⚙ Impostazioni
            </Link>
            <button onClick={handleAddOffer} className="btn btn--secondary">
              + Aggiungi Offerta
            </button>
            <button
              onClick={handleSaveChanges}
              className="btn btn--primary"
              disabled={!hasChanges || saving}
            >
              {saving ? "Salvataggio..." : "Applica"}
            </button>
            <button onClick={handleLogout} className="btn btn--outline">
              Logout
            </button>
          </div>
        </div>

        {deleteConfirm && (
          <div className="delete-confirm-overlay">
            <div className="delete-confirm-box">
              <p>Sei sicuro di voler eliminare questa offerta?</p>
              <div className="delete-confirm-actions">
                <button
                  onClick={() => handleDeleteOffer(deleteConfirm)}
                  className="btn btn--danger"
                >
                  Elimina
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="btn btn--secondary"
                >
                  Annulla
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="offers-grid">
          {offers.map((offer) => (
            <OfferCardAdmin
              key={offer.id}
              offer={offer}
              onDeleteClick={(id) => setDeleteConfirm(id)}
              onImageUpload={handleImageUpload}
              onFieldChange={handleFieldChange}
            />
          ))}
        </div>

        {offers.length === 0 && (
          <div className="empty-message">
            <p>
              Nessuna offerta presente. Clicca su "Aggiungi Offerta" per
              iniziare.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OffertAdmin;
